import { useState } from 'react';
import { useTags, useCreateTag, useUpdateTag, useDeleteTag, useSendEmail } from '../../hooks/useTags';
import { Tag } from '../../types';
import { 
  Plus, Edit, Trash2, Search, Mail, User, Link as LinkIcon, 
  Copy, Send, ExternalLink, Download, Check, X 
} from 'lucide-react';
import LoadingSpinner from '../shared/LoadingSpinner';
import Tooltip from '../shared/Tooltip';
import Select from '../shared/Select';
import toast from 'react-hot-toast';

export default function TagsAndAccess() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'with-email' | 'with-access' | 'ready'>('all');
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagEmail, setNewTagEmail] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTag, setEmailTag] = useState<Tag | null>(null);
  const [customMessage, setCustomMessage] = useState('');

  const { data: tagsData, isLoading, error } = useTags({ search: searchTerm });
  const createTagMutation = useCreateTag();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();
  const sendEmailMutation = useSendEmail();

  const tags = tagsData?.data || [];

  const filteredTags = tags.filter(tag => {
    switch (filter) {
      case 'with-email':
        return tag.email;
      case 'with-access':
        return true; // Tous les tags ont maintenant automatiquement un accès
      case 'ready':
        return tag.email; // Prêts = avec email (accès automatique)
      default:
        return true;
    }
  });

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTagName.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      await createTagMutation.mutateAsync({
        name: newTagName.trim(),
        email: newTagEmail.trim() || undefined,
      });
      
      toast.success('Tag créé avec succès');
      setNewTagName('');
      setNewTagEmail('');
      setShowCreateForm(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const handleUpdateTag = async (tag: Tag, updates: { name?: string; email?: string }) => {
    try {
      await updateTagMutation.mutateAsync({ tagId: tag.id, updates });
      toast.success('Tag mis à jour');
      setEditingTag(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTag = async (tag: Tag) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${tag.name}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      await deleteTagMutation.mutateAsync(tag.id);
      toast.success('Tag supprimé');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };


  const handleCopyLink = async (tag: Tag) => {
    if (!tag.accessUuid) {
      toast.error('Aucun lien d\'accès disponible');
      return;
    }

    const url = `${window.location.origin}/access/${tag.accessUuid}`;
    
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copié dans le presse-papier');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Lien copié dans le presse-papier');
    }
  };

  const handleSendEmail = (tag: Tag) => {
    if (!tag.email || !tag.accessUuid) {
      toast.error('Email ou lien d\'accès manquant');
      return;
    }

    setEmailTag(tag);
    setCustomMessage('');
    setShowEmailModal(true);
  };

  const handleSendEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailTag) return;
    
    try {
      await sendEmailMutation.mutateAsync({
        tagId: emailTag.id,
        customMessage: customMessage.trim()
      });
      
      toast.success('Email envoyé avec succès');
      handleCloseEmailModal();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'envoi de l\'email');
    }
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailTag(null);
    setCustomMessage('');
  };

  const handleExportCSV = () => {
    const csvContent = [
      ['Nom', 'Email', 'Photos', 'Lien d\'accès'].join(','),
      ...filteredTags.map(tag => [
        `"${tag.name}"`,
        `"${tag.email || ''}"`,
        tag.photoCount,
        tag.accessUuid ? `"${window.location.origin}/access/${tag.accessUuid}"` : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tags-access-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Export CSV téléchargé');
  };

  if (isLoading) {
    return <LoadingSpinner size="large" text="Chargement des tags..." />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Erreur lors du chargement des tags</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 btn-primary"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const stats = {
    total: tags.length,
    withEmail: tags.filter(tag => tag.email).length,
    withAccess: tags.length, // Tous les tags ont maintenant automatiquement un accès
    ready: tags.filter(tag => tag.email).length, // Prêts = avec email (accès automatique)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Tags et Accès</h1>
          <p className="mt-2 text-gray-600">
            Gérez les tags et emails. Les liens d'accès sécurisés sont générés automatiquement.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Tooltip content="Exporter la liste des tags et leurs informations en format CSV">
            <button
              onClick={handleExportCSV}
              disabled={filteredTags.length === 0}
              className="btn-outline disabled:opacity-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </Tooltip>
          <Tooltip content="Créer un nouveau tag pour identifier un élève ou un groupe">
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Tag
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center">
            <User className="h-8 w-8 text-gray-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-600">Total tags</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <Mail className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold text-gray-900">{stats.withEmail}</p>
              <p className="text-sm text-gray-600">Avec email</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <LinkIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold text-gray-900">{stats.withAccess}</p>
              <p className="text-sm text-gray-600">Avec accès</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <Send className="h-8 w-8 text-primary-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold text-gray-900">{stats.ready}</p>
              <p className="text-sm text-gray-600">Prêts à envoyer</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un tag..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
        <Tooltip content="Filtrer les tags selon leur configuration">
          <Select
            value={filter}
            onChange={(value) => setFilter(value as any)}
            options={[
              { value: 'all', label: 'Tous les tags' },
              { value: 'with-email', label: 'Avec email' },
              { value: 'with-access', label: 'Avec accès' },
              { value: 'ready', label: 'Prêts à envoyer' }
            ]}
            className="w-auto min-w-[140px]"
            size="md"
            aria-label="Filtrer les tags selon leur configuration"
          />
        </Tooltip>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Créer un nouveau tag</h3>
          <form onSubmit={handleCreateTag} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Nom *
              </label>
              <input
                type="text"
                id="name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="mt-1 input"
                placeholder="Ex: Jean Dupont"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email (optionnel)
              </label>
              <input
                type="email"
                id="email"
                value={newTagEmail}
                onChange={(e) => setNewTagEmail(e.target.value)}
                className="mt-1 input"
                placeholder="parent@email.com"
              />
            </div>
            <div className="flex space-x-3">
              <Tooltip content="Créer le nouveau tag avec les informations saisies">
                <button
                  type="submit"
                  disabled={createTagMutation.isLoading}
                  className="btn-primary disabled:opacity-50"
                >
                  {createTagMutation.isLoading ? 'Création...' : 'Créer'}
                </button>
              </Tooltip>
              <Tooltip content="Annuler la création et fermer le formulaire">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewTagName('');
                    setNewTagEmail('');
                  }}
                  className="btn-outline"
                >
                  Annuler
                </button>
              </Tooltip>
            </div>
          </form>
        </div>
      )}

      {/* Tags List */}
      {filteredTags.length === 0 ? (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchTerm || filter !== 'all' ? 'Aucun tag trouvé' : 'Aucun tag'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filter !== 'all' 
              ? 'Essayez de modifier vos filtres de recherche.'
              : 'Commencez par créer votre premier tag.'
            }
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tag
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Photos
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTags.map((tag) => (
                    <TagRow
                      key={tag.id}
                      tag={tag}
                      isEditing={editingTag?.id === tag.id}
                      onEdit={() => setEditingTag(tag)}
                      onCancelEdit={() => setEditingTag(null)}
                      onUpdate={(updates) => handleUpdateTag(tag, updates)}
                      onDelete={() => handleDeleteTag(tag)}
                      onCopyLink={() => handleCopyLink(tag)}
                      onSendEmail={() => handleSendEmail(tag)}
                      isUpdating={updateTagMutation.isLoading}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {filteredTags.map((tag) => (
              <TagCard
                key={tag.id}
                tag={tag}
                isEditing={editingTag?.id === tag.id}
                onEdit={() => setEditingTag(tag)}
                onCancelEdit={() => setEditingTag(null)}
                onUpdate={(updates) => handleUpdateTag(tag, updates)}
                onDelete={() => handleDeleteTag(tag)}
                onCopyLink={() => handleCopyLink(tag)}
                onSendEmail={() => handleSendEmail(tag)}
                isUpdating={updateTagMutation.isLoading}
              />
            ))}
          </div>
        </>
      )}

      {/* Email Modal */}
      {showEmailModal && emailTag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                📧 Envoyer l'email d'accès
              </h3>
              <button 
                onClick={handleCloseEmailModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center mb-2">
                <Mail className="h-5 w-5 text-blue-600 mr-2" />
                <span className="font-medium text-blue-900">Destinataire</span>
              </div>
              <p className="text-sm text-blue-800">
                <strong>{emailTag.name}</strong> - {emailTag.email}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Un email sera envoyé avec le lien d'accès sécurisé
              </p>
            </div>

            <form onSubmit={handleSendEmailSubmit}>
              <div className="mb-4">
                <label htmlFor="customMessage" className="block text-sm font-medium text-gray-700 mb-2">
                  Message personnalisé (optionnel)
                </label>
                <textarea
                  id="customMessage"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={6}
                  className="input resize-none"
                  placeholder="Laissez vide pour utiliser le message par défaut, ou personnalisez votre message ici..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si vide, un message par défaut sera utilisé avec toutes les instructions nécessaires.
                </p>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">📧 L'email inclura automatiquement :</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• ✅ Lien d'accès sécurisé et personnalisé</li>
                  <li>• 📱 Instructions pour télécharger les photos</li>
                  <li>• ⭐ Information sur les demandes UltraHD</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={sendEmailMutation.isLoading}
                  className="flex-1 btn-primary disabled:opacity-50 flex items-center justify-center"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sendEmailMutation.isLoading ? 'Envoi en cours...' : 'Envoyer l\'email'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseEmailModal}
                  className="btn-outline"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

interface TagRowProps {
  tag: Tag;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onUpdate: (updates: { name?: string; email?: string }) => void;
  onDelete: () => void;
  onCopyLink: () => void;
  onSendEmail: () => void;
  isUpdating: boolean;
}

function TagRow({ 
  tag, 
  isEditing, 
  onEdit, 
  onCancelEdit, 
  onUpdate, 
  onDelete, 
  onCopyLink,
  onSendEmail,
  isUpdating
}: TagRowProps) {
  const [editName, setEditName] = useState(tag.name);
  const [editEmail, setEditEmail] = useState(tag.email || '');

  const handleSave = () => {
    const updates: { name?: string; email?: string } = {};
    
    if (editName !== tag.name) {
      updates.name = editName;
    }
    
    if (editEmail !== (tag.email || '')) {
      updates.email = editEmail;
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(updates);
    } else {
      onCancelEdit();
    }
  };


  if (isEditing) {
    return (
      <tr>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="input text-sm"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            className="input text-sm"
            placeholder="email@example.com"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {tag.photoCount}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
          <Tooltip content="Sauvegarder les modifications">
            <button
              onClick={handleSave}
              disabled={isUpdating}
              className="text-green-600 hover:text-green-700 disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
            </button>
          </Tooltip>
          <Tooltip content="Annuler les modifications">
            <button
              onClick={onCancelEdit}
              className="text-red-600 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </button>
          </Tooltip>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{tag.name}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {tag.email || <span className="text-gray-400 italic">Non défini</span>}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {tag.photoCount}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
        <div className="flex items-center justify-end space-x-2">
          <Tooltip content="Modifier le nom et l'email de ce tag">
            <button
              onClick={onEdit}
              className="text-blue-600 hover:text-blue-700"
            >
              <Edit className="h-4 w-4" />
            </button>
          </Tooltip>
          
          {/* Actions disponibles pour tous les tags (accès automatique) */}
          {tag.accessUuid && (
            <>
              <Tooltip content="Copier le lien d'accès dans le presse-papier">
                <button
                  onClick={onCopyLink}
                  className="text-green-600 hover:text-green-700"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </Tooltip>
              
              {tag.email && (
                <Tooltip content="Envoyer un email avec le lien d'accès">
                  <button
                    onClick={onSendEmail}
                    className="text-purple-600 hover:text-purple-700"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </Tooltip>
              )}
              
              <Tooltip content="Ouvrir le lien d'accès dans un nouvel onglet">
                <a
                  href={`/access/${tag.accessUuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-700"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Tooltip>
            </>
          )}
          
          <Tooltip content="Supprimer définitivement ce tag et son accès">
            <button
              onClick={onDelete}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

interface TagCardProps {
  tag: Tag;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onUpdate: (updates: { name?: string; email?: string }) => void;
  onDelete: () => void;
  onCopyLink: () => void;
  onSendEmail: () => void;
  isUpdating: boolean;
}

function TagCard({ 
  tag, 
  isEditing, 
  onEdit, 
  onCancelEdit, 
  onUpdate, 
  onDelete, 
  onCopyLink,
  onSendEmail,
  isUpdating
}: TagCardProps) {
  const [editName, setEditName] = useState(tag.name);
  const [editEmail, setEditEmail] = useState(tag.email || '');

  const handleSave = () => {
    const updates: { name?: string; email?: string } = {};
    
    if (editName !== tag.name) {
      updates.name = editName;
    }
    
    if (editEmail !== (tag.email || '')) {
      updates.email = editEmail;
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(updates);
    } else {
      onCancelEdit();
    }
  };

  if (isEditing) {
    return (
      <div className="card p-4">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="input text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              className="input text-sm"
              placeholder="email@example.com"
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">{tag.photoCount} photos</span>
            <div className="flex space-x-2">
              <Tooltip content="Sauvegarder les modifications">
                <button
                  onClick={handleSave}
                  disabled={isUpdating}
                  className="p-2 text-green-600 hover:text-green-700 disabled:opacity-50 bg-green-50 hover:bg-green-100 rounded-full transition-colors"
                >
                  <Check className="h-4 w-4" />
                </button>
              </Tooltip>
              <Tooltip content="Annuler les modifications">
                <button
                  onClick={onCancelEdit}
                  className="p-2 text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-full transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <div className="flex flex-col space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate">{tag.name}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {tag.email || <span className="italic">Email non défini</span>}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {tag.photoCount} photos
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
          <Tooltip content="Modifier ce tag">
            <button
              onClick={onEdit}
              className="flex items-center px-3 py-2 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
            >
              <Edit className="h-4 w-4 mr-1" />
              Modifier
            </button>
          </Tooltip>
          
          {tag.accessUuid && (
            <>
              <Tooltip content="Copier le lien d'accès">
                <button
                  onClick={onCopyLink}
                  className="flex items-center px-3 py-2 text-sm bg-green-50 text-green-700 hover:bg-green-100 rounded-md transition-colors"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copier
                </button>
              </Tooltip>
              
              {tag.email && (
                <Tooltip content="Envoyer un email avec le lien">
                  <button
                    onClick={onSendEmail}
                    className="flex items-center px-3 py-2 text-sm bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-md transition-colors"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Envoyer
                  </button>
                </Tooltip>
              )}
              
              <Tooltip content="Ouvrir dans un nouvel onglet">
                <a
                  href={`/access/${tag.accessUuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-2 text-sm bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Ouvrir
                </a>
              </Tooltip>
            </>
          )}
          
          <Tooltip content="Supprimer ce tag">
            <button
              onClick={onDelete}
              className="flex items-center px-3 py-2 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded-md transition-colors ml-auto"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Supprimer
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}